import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import axios from "axios";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Camera, MapPin, Globe, Mail, Phone, Building2, FileText, CheckCircle2, Edit, Plus, X, Upload, Trash2, Shield, Award, Video, Image as ImageIcon, Briefcase, Loader2, Mic, ChevronDown } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import PendingApprovalModal from "@/components/PendingApprovalModal";
import ProfileSubmittedModal from "@/components/ProfileSubmittedModal";
import ReviewsList from "@/components/ReviewsList";
import PortfolioGallery from "@/components/PortfolioGallery";
import WelcomeModal from "@/components/WelcomeModal";
import { loadGoogleMapsScript } from '@/utils/googleMapsLoader';
import { initPlaceAutocomplete, destroyPlaceAutocomplete } from '@/utils/placeAutocomplete';
import "@/styles/commandDeck.css";

const Profile = ({ onLogout }) => {
  const { isDark } = useTheme();
  const location = useLocation();
  const initialUser = JSON.parse(localStorage.getItem('shootzu_user') || '{}');
  const [user, setUser] = useState(initialUser);
  // Role options matching signup form
  const roleOptions = [
    { value: "videographer", label: "Videographer", icon: Video },
    { value: "photographer", label: "Photographer", icon: Camera },
    { value: "editor_photos", label: "Editor (Photos)", icon: ImageIcon },
    { value: "editor_video", label: "Editor (Video)", icon: Video },
    { value: "assistant", label: "Assistant", icon: Briefcase },
    { value: "drone_operator", label: "Drone Operator", icon: Shield },
    { value: "voxpop_videographer", label: "VoxPop Videographer", icon: Mic }
  ];
  
  // Check if redirected from incomplete profile modal
  const initialEditMode = location.state?.editMode || false;
  
  const [isEditMode, setIsEditMode] = useState(initialEditMode);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [profilePhotoUrl, setProfilePhotoUrl] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [showSubmittedModal, setShowSubmittedModal] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [userStatus, setUserStatus] = useState(user.status || '');
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState({ average_rating: 0, total_reviews: 0, rating_breakdown: {} });
  const [portfolioImages, setPortfolioImages] = useState([]);
  const [portfolioImageKeys, setPortfolioImageKeys] = useState([]);
  const [abnValidating, setAbnValidating] = useState(false);
  const [abnResult, setAbnResult] = useState(null);
  const [businessNames, setBusinessNames] = useState([]);
  const [abnError, setAbnError] = useState('');
  
  const [formData, setFormData] = useState({
    first_name: user.first_name || '',
    last_name: user.last_name || '',
    full_name: user.full_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || '',
    email: user.email || '',
    phone: user.phone || '',
    business_name: user.business_name || '',
    address: user.address || user.location || '',
    location_lat: user.location_lat || null,  // Add coordinates
    location_lon: user.location_lon || null,  // Add coordinates
    role: user.role || '',
    skills: user.skills || [],
    bio: user.bio || '',
    abn: user.abn || '',
    gst_registered: user.gst_registered || false,
    website_url: user.website_url || '',
    facebook_url: user.facebook_url || '',
    instagram_url: user.instagram_url || '',
    portfolio_links: user.portfolio_links || [],
    video_links: user.video_links || [],
    editing_links: user.editing_links || [],
    equipment_list: user.equipment_list || [],
    casa_repl: user.casa_repl === true,
    casa_reoc: user.casa_reoc === true,
    wwcc: user.wwcc === true
  });

  // Fetch latest user data on mount to pre-fill form
  useEffect(() => {
    const fetchUserData = async () => {
      if (user.id) {
        try {
          console.log('🔄 Fetching user data for:', user.id);
          const token = localStorage.getItem('shootzu_token');
          const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/profile/${user.id}`, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
          });
          const userData = response.data;
          
          console.log('✅ User data received:', {
            first_name: userData.first_name,
            last_name: userData.last_name,
            full_name: userData.full_name,
            email: userData.email,
            address: userData.address
          });
          
          // Split full_name if first_name and last_name are missing
          let firstName = userData.first_name;
          let lastName = userData.last_name;
          
          if (!firstName && !lastName && userData.full_name) {
            const nameParts = userData.full_name.trim().split(' ');
            firstName = nameParts[0] || '';
            lastName = nameParts.slice(1).join(' ') || '';
            console.log('📝 Split full_name into:', { firstName, lastName });
          }
          
          // Update formData with latest user data
          setFormData(prev => ({
            ...prev,
            first_name: firstName || prev.first_name || '',
            last_name: lastName || prev.last_name || '',
            full_name: userData.full_name || `${firstName || ''} ${lastName || ''}`.trim() || prev.full_name,
            email: userData.email || prev.email,
            phone: userData.phone || prev.phone,
            business_name: userData.business_name || prev.business_name,
            address: userData.address || userData.location || prev.address,
            location_lat: userData.location_lat || prev.location_lat,
            location_lon: userData.location_lon || prev.location_lon,
            role: userData.role || prev.role,
            skills: userData.skills || prev.skills,
            bio: userData.bio || prev.bio,
            abn: userData.abn || prev.abn,
            gst_registered: userData.gst_registered !== undefined ? userData.gst_registered : prev.gst_registered,
            website_url: userData.website_url || prev.website_url,
            facebook_url: userData.facebook_url || prev.facebook_url,
            instagram_url: userData.instagram_url || prev.instagram_url,
            portfolio_links: userData.portfolio_links || prev.portfolio_links,
            video_links: userData.video_links || prev.video_links,
            editing_links: userData.editing_links || prev.editing_links,
            equipment_list: userData.equipment_list || prev.equipment_list,
            casa_repl: userData.casa_repl === true,
            casa_reoc: userData.casa_reoc === true,
            wwcc: userData.wwcc === true
          }));
          
          console.log('✅ FormData updated');

          // Update user state with API data (includes user_number)
          setUser(prev => ({
            ...prev,
            ...userData,
            first_name: firstName || userData.first_name,
            last_name: lastName || userData.last_name
          }));

          // Update profile photo
          if (userData.profile_photo_url) {
            setProfilePhotoUrl(userData.profile_photo_url);
          }

          // Update user status from DATABASE
          const dbStatus = (userData.status || '').toUpperCase();
          console.log('📊 Profile - User status from DB:', dbStatus);
          setUserStatus(dbStatus);
          
          // Auto-enable edit mode for NEW users (first time setup)
          if (dbStatus === 'NEW') {
            console.log('🔧 NEW user detected - enabling edit mode');
            setIsEditMode(true);
            console.log('✅ Showing Welcome Modal for NEW user on profile');
            setShowWelcomeModal(true);
          }
          
          // Show pending modal if status is PENDING_APPROVAL
          if (dbStatus === 'PENDING_APPROVAL') {
            setShowPendingModal(true);
          }
        } catch (error) {
          console.error('❌ Error fetching user data:', error);
        }
      }
    };

    fetchUserData();
  }, [user.id]);

  // Store autocomplete instance ref
  const autocompleteRef = useRef(null);

  // Initialize Google Places Autocomplete for address
  useEffect(() => {
    if (!isEditMode) return;

    let mounted = true;

    // Always clear ref when entering edit mode (DOM is fresh)
    autocompleteRef.current = null;

    loadGoogleMapsScript(() => {
      setTimeout(() => {
        if (!mounted) return;

        const container = document.getElementById('profile-address-autocomplete');
        if (!container) return;

        try {
          const result = initPlaceAutocomplete(
            'profile-address-autocomplete',
            (place) => {
              if (place && place.formatted_address) {
                setFormData(prev => ({
                  ...prev,
                  address: place.formatted_address,
                  location_lat: place.lat || null,
                  location_lon: place.lng || null
                }));
                toast.success('Address verified!');
              }
            }
          );

          if (result) {
            autocompleteRef.current = result.autocomplete;
          }
        } catch (error) {
          console.error('Error initializing address autocomplete:', error);
        }
      }, 500);
    });

    return () => {
      mounted = false;
      try {
        destroyPlaceAutocomplete('profile-address-autocomplete');
        autocompleteRef.current = null;
      } catch (e) {}
    };
  }, [isEditMode]);

  // Close the Edit Profile modal on Escape
  useEffect(() => {
    if (!isEditMode) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setIsEditMode(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isEditMode]);

  const handleValidateABN = async (abnValue) => {
    const abnDigits = abnValue.replace(/\s/g, '');
    
    if (abnDigits.length !== 11) {
      toast.error('ABN must be 11 digits');
      return;
    }
    
    try {
      setAbnValidating(true);
      
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/abn/validate`,
        { abn: abnDigits }
      );
      
      if (response.data.is_valid) {
        setAbnResult(response.data);
        const names = response.data.business_names || [];
        setBusinessNames(names);
        
        // Auto-populate fields from API response
        if (names.length === 1) {
          // Only one business name, auto-select it
          setFormData(prev => ({
            ...prev,
            business_name: names[0],
            gst_registered: response.data.gst_registered
          }));
        } else if (names.length > 1) {
          // Multiple names, need user to select
          setFormData(prev => ({
            ...prev,
            gst_registered: response.data.gst_registered
          }));
        }
        
        toast.success('ABN validated successfully!');
      } else {
        toast.error(response.data.error || 'Invalid ABN');
        setAbnResult(null);
        setBusinessNames([]);
      }
    } catch (err) {
      console.error('ABN validation error:', err);
      toast.error(err.response?.data?.detail || 'Failed to validate ABN');
      setAbnResult(null);
      setBusinessNames([]);
    } finally {
      setAbnValidating(false);
    }
  };

  const formatABN = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 2) return digits;
    if (digits.length <= 5) return `${digits.slice(0, 2)} ${digits.slice(2)}`;
    if (digits.length <= 8) return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5)}`;
    return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 8)} ${digits.slice(8, 11)}`;
  };

  const fetchProfilePhoto = useCallback(async () => {
    try {
      // Always fetch from profile endpoint first (works for both migrated and non-migrated)
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/profile/${user.id}`);
      if (response.data.profile_photo_url) {
        setProfilePhotoUrl(response.data.profile_photo_url);
      }
      
      // Update formData with latest API data (including equipment_list, portfolio_links, etc.)
      if (response.data) {
        setFormData(prev => ({
          ...prev,
          equipment_list: response.data.equipment_list || prev.equipment_list || [],
          portfolio_links: response.data.portfolio_links || prev.portfolio_links || [],
          video_links: response.data.video_links || prev.video_links || [],
          editing_links: response.data.editing_links || prev.editing_links || [],
          facebook_url: response.data.facebook_url || prev.facebook_url || '',
          instagram_url: response.data.instagram_url || prev.instagram_url || '',
          casa_repl: response.data.casa_repl !== undefined ? response.data.casa_repl : prev.casa_repl,
          casa_reoc: response.data.casa_reoc !== undefined ? response.data.casa_reoc : prev.casa_reoc,
          wwcc: response.data.wwcc !== undefined ? response.data.wwcc : prev.wwcc
        }));
      }
      
      // Fallback checks if profile_photo_url not in API response
      if (!response.data.profile_photo_url) {
        // Fallback: Check if user object has profile_photo_url
        if (user.profile_photo_url) {
          setProfilePhotoUrl(user.profile_photo_url);
          return;
        }
        
        // Final fallback: Fetch from S3 photo endpoint
        const photoResponse = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/profile/photo/${user.id}`);
        if (photoResponse.data.photo_url) {
          setProfilePhotoUrl(photoResponse.data.photo_url);
        }
      }
    } catch (error) {
      console.error('Error fetching profile photo:', error);
    }
  }, [user.id, user.profile_photo_url]);


  const fetchReviews = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/reviews/${user.id}`);
      if (response.data.success) {
        setReviews(response.data.reviews);
        setReviewStats(response.data.stats);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  }, [user.id]);
  
  const fetchPortfolioImages = useCallback(async () => {
    try {
      const token = localStorage.getItem('shootzu_token');
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/profile/${user.id}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      console.log('Portfolio response:', response.data);
      if (response.data.portfolio_images) {
        console.log('Setting portfolio images:', response.data.portfolio_images.length);
        setPortfolioImages(response.data.portfolio_images);
        setPortfolioImageKeys(response.data.portfolio_image_keys || []);
      }
    } catch (error) {
      console.error('Error fetching portfolio images:', error);
    }
  }, [user.id]);

  useEffect(() => {
    fetchProfilePhoto();
    // Load Google Maps script from admin settings
    loadGoogleMapsScript();
    fetchReviews();
    fetchPortfolioImages();
  }, [fetchReviews, fetchPortfolioImages, fetchProfilePhoto]);

  // Initialize ABN validation status from existing profile data (only on mount)
  const [abnInitialized, setAbnInitialized] = useState(false);
  useEffect(() => {
    if (!abnInitialized && formData.abn && formData.business_name && formData.gst_registered !== null && !abnResult) {
      // User already has validated ABN data, restore validation status ONLY if not already set
      setAbnResult({
        is_valid: true,
        gst_registered: formData.gst_registered,
        business_names: [formData.business_name]
      });
      setBusinessNames([formData.business_name]);
      setAbnInitialized(true);
    }
  }, [abnInitialized, formData.abn, formData.business_name, formData.gst_registered, abnResult]);
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPEG, PNG, GIF, or WebP');
      return;
    }

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error('File too large. Maximum size is 10MB');
      return;
    }

    // Immediately upload the photo
    setUploadingPhoto(true);
    const token = localStorage.getItem('shootzu_token');
    if (!token) {
      toast.error('Please log in to upload photos');
      setUploadingPhoto(false);
      return;
    }

    try {
      // Step 1: Get presigned URL
      const uploadUrlResponse = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/profile/photo/upload-url`,
        null,
        {
          params: {
            user_id: user.id,
            filename: file.name,
            content_type: file.type
          },
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      const { url, fields, object_key } = uploadUrlResponse.data;

      // Step 2: Upload to S3 (direct to S3, no auth header needed)
      const formData = new FormData();
      Object.entries(fields).forEach(([key, value]) => {
        formData.append(key, value);
      });
      formData.append('file', file);

      await axios.post(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Step 3: Confirm upload
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/profile/photo/confirm`,
        null,
        {
          params: {
            user_id: user.id,
            object_key: object_key
          },
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      toast.success('Profile photo uploaded successfully!');
      fetchProfilePhoto();
    } catch (error) {
      console.error('Photo upload error:', error);
      toast.error('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handlePhotoUpload = async () => {
    // This function is kept for backward compatibility but no longer used
    // Photo uploads now happen immediately in handleFileSelect
    toast.info('Photo upload is automatic when you select a file');
  };

  const handleDeletePhoto = async () => {
    if (!window.confirm('Delete profile photo?')) return;

    const token = localStorage.getItem('shootzu_token');
    if (!token) {
      toast.error('Please log in to delete photos');
      return;
    }

    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/profile/photo/${user.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setProfilePhotoUrl(null);
      toast.success('Profile photo deleted');
    } catch (error) {
      console.error('Delete photo error:', error);
      toast.error('Failed to delete photo');
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Role/Skills handlers
  const handleRoleToggle = (role) => {
    setFormData(prev => {
      const currentSkills = prev.skills || [];
      const hasRole = currentSkills.includes(role);
      
      if (hasRole) {
        // Remove role
        return { ...prev, skills: currentSkills.filter(s => s !== role) };
      } else {
        // Add role
        return { ...prev, skills: [...currentSkills, role] };
      }
    });
  };

  // Portfolio links handlers
  const handleAddLink = (field) => {
    handleChange(field, [...formData[field], '']);
  };

  const handleRemoveLink = (field, index) => {
    handleChange(field, formData[field].filter((_, i) => i !== index));
  };

  const handleLinkChange = (field, index, value) => {
    const newLinks = [...formData[field]];
    newLinks[index] = value;
    handleChange(field, newLinks);
  };

  const validatePortfolio = () => {
    const errors = [];

    // Check if role requires portfolio - REMOVED minimum link requirements
    const skills = formData.skills || [];

    // Only validate CASA certifications for drone operators
    if (skills.includes('drone_operator')) {
      if (!formData.casa_repl && !formData.casa_reoc) {
        errors.push('Drone Operators must select at least one CASA certification (RepL or ReOc)');
      }
    }

    return errors;
  };

  // Track if save was explicitly clicked
  const [saveButtonClicked, setSaveButtonClicked] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('=== FORM SUBMIT TRIGGERED ===');
    console.log('Save button clicked flag:', saveButtonClicked);
    console.log('Is edit mode:', isEditMode);
    
    // CRITICAL: Only process if save button was explicitly clicked
    if (!saveButtonClicked) {
      console.log('❌ Submit blocked: Save button was not clicked');
      return;
    }
    
    // Reset the flag
    setSaveButtonClicked(false);
    console.log('✅ Processing form submission');

    // Validate mandatory fields
    if (!formData.first_name?.trim() || !formData.last_name?.trim()) {
      toast.error('First name and last name are required');
      return;
    }
    if (!formData.business_name?.trim()) {
      toast.error('Business Name is required');
      return;
    }
    if (!formData.phone?.trim()) {
      toast.error('Phone number is required');
      return;
    }
    if (!formData.address?.trim()) {
      toast.error('Address is required');
      return;
    }
    if (!formData.abn?.trim()) {
      toast.error('ABN is required');
      return;
    }
    
    if (!abnResult || !abnResult.is_valid) {
      toast.error('Please validate your ABN first');
      return;
    }
    
    if (!formData.business_name?.trim()) {
      toast.error('Business name is required');
      return;
    }
    if (!formData.skills || formData.skills.length === 0) {
      toast.error('Please select at least one role');
      return;
    }

    // Validate portfolio requirements
    const validationErrors = validatePortfolio();
    if (validationErrors.length > 0) {
      validationErrors.forEach(error => toast.error(error));
      return;
    }

    // Check if this is first-time submission from NEW user
    const isNewUser = userStatus === 'NEW';

    // NEW users must have profile photo and bio before submitting for approval
    if (isNewUser) {
      if (!profilePhotoUrl) {
        toast.error('Profile photo is required before submitting for approval');
        return;
      }
      if (!formData.bio?.trim() || formData.bio.trim().length < 50) {
        toast.error('Please add a bio (minimum 50 characters) before submitting for approval');
        return;
      }
    }

    const token = localStorage.getItem('shootzu_token');
    if (!token) {
      toast.error('Please log in to update your profile');
      return;
    }

    try {

      // Update user profile in backend
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/${user.id}`,
        {
          ...formData,
          // If NEW user, mark profile as submitted (triggers status change in backend)
          profile_submitted: isNewUser ? true : formData.profile_submitted
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      // If this was a NEW user submitting for first time
      if (isNewUser) {
        // Send admin notification email
        try {
          await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/api/admin/notify-profile-submission`,
            {
              user_id: user.id,
              user_name: `${formData.first_name} ${formData.last_name}`,
              user_email: formData.email
            },
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
        } catch (emailError) {
          console.error('Failed to send admin notification:', emailError);
          // Don't block the submission if email fails
        }

        // Show submission success modal
        setShowSubmittedModal(true);
        setUserStatus('PENDING_APPROVAL');
        
        // Update localStorage
        const updatedUser = { 
          ...user, 
          ...formData,
          status: 'PENDING_APPROVAL'
        };
        localStorage.setItem('shootzu_user', JSON.stringify(updatedUser));
      } else {
        toast.success('Profile updated successfully!');
      }
      
      setIsEditMode(false);
      
      // Update local storage if not NEW user
      if (!isNewUser) {
        const updatedUser = { 
          ...user, 
          ...formData
        };
        localStorage.setItem('shootzu_user', JSON.stringify(updatedUser));
      }
    } catch (error) {
      console.error('Profile update error:', error);
      toast.error('Failed to update profile');
    }
  };

  const initials = `${formData.first_name?.[0] || ''}${formData.last_name?.[0] || ''}`.toUpperCase() || 'U';

  const isPhotographer = formData.skills?.includes('photographer');
  const isVideographer = formData.skills?.includes('videographer');
  const isEditor = formData.skills?.includes('editor_photos') || formData.skills?.includes('editor_video');
  const isDroneOperator = formData.skills?.includes('drone_operator');
  
  // Extract city/town from full address
  const getCityFromAddress = (address) => {
    if (!address) return '';
    // Split by comma and get the part with state (e.g., "Barwon Heads VIC")
    const parts = address.split(',').map(p => p.trim());
    // Find part with VIC, NSW, QLD, SA, WA, TAS, NT, ACT
    const statePattern = /\b(VIC|NSW|QLD|SA|WA|TAS|NT|ACT)\b/;
    const cityPart = parts.find(p => statePattern.test(p));
    return cityPart || parts[0] || '';
  };
  
  // Shared modal field-label style
  const labelStyle = { display: 'block', fontSize: 11.5, color: 'rgba(255,255,255,.5)', marginBottom: 6 };
  const sectionLabelStyle = { fontSize: 11, fontWeight: 700, letterSpacing: '.6px', color: 'rgba(255,255,255,.4)' };
  const dividerStyle = { height: 1, background: 'rgba(255,255,255,.07)' };

  const scrollToReviews = () => {
    const reviewsSection = document.getElementById('reviews-section');
    if (reviewsSection) {
      reviewsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen" style={{ background: '#0A0E1A' }}>
      <Sidebar onLogout={onLogout} />
      <Header />

      {/* Main Content - Command Deck */}
      <div className="cd-root sidebar-content">
        <style>{`
          @keyframes profSpin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
          @keyframes profModalIn { from { opacity: 0; transform: translateY(14px) scale(.98); } to { opacity: 1; transform: none; } }
          .prof-grid { display: grid; grid-template-columns: 1fr 340px; gap: 18px; }
          @media (max-width: 900px) { .prof-grid { grid-template-columns: 1fr; } }
          .prof-hero { height: 340px; }
          @media (max-width: 900px) { .prof-hero { height: 230px; } }
          /* Mobile: identity row stacks (cd-col-mobile). Stop flex-basis:340px from
             becoming a tall fixed HEIGHT (was leaving a giant gap), and left-align roles. */
          @media (max-width: 720px) {
            .prof-id-text { flex: 1 1 auto !important; }
            .prof-id-roles { margin-left: 0 !important; justify-content: flex-start !important; }
          }
          .prof-2col { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
          @media (max-width: 520px) { .prof-2col { grid-template-columns: 1fr; } }
          .prof-abn-select { width: 100%; appearance: none; -webkit-appearance: none; padding: 10px 34px 10px 13px; border: 1px solid rgba(255,255,255,.16); border-radius: 10px; background: rgba(12,17,32,.5); color: #fff; font-family: 'Work Sans', sans-serif; font-size: 13.5px; font-weight: 600; outline: none; cursor: pointer; }
          .prof-abn-select:focus { border-color: rgba(40,192,208,.5); }
          @media (prefers-reduced-motion: reduce) { .prof-watermark, .prof-modal-card { animation: none !important; } }
        `}</style>
        <div className="cd-bloom-wrap">
          <div className="cd-bloom cd-bloom-1" />
          <div className="cd-bloom cd-bloom-2" />
        </div>

        {/* ===== HERO — full-width cover band (matches dashboard) ===== */}
        {(() => { const coverPhoto = portfolioImages?.[0] || null; return (
          <div className="prof-hero" style={{ position: 'relative', zIndex: 1, borderRadius: 0, overflow: 'hidden', background: 'linear-gradient(120deg,#572B7C,#0C1120 55%,#78287C)' }}>
            {coverPhoto && (
              <div style={{ position: 'absolute', inset: 0, backgroundImage: `url(${coverPhoto})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
            )}
            {/* brand gradient (low opacity over photo) + dark scrim for legibility */}
            {coverPhoto && (
              <>
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(120deg,#572B7C,#0C1120 55%,#78287C)', opacity: 0.18 }} />
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(12,17,32,0) 38%, rgba(12,17,32,.4) 74%, rgba(12,17,32,.8))' }} />
              </>
            )}
            <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(600px 300px at 80% 0%, rgba(40,192,208,.3), transparent 60%)' }} />
            <img className="prof-watermark" src="/assets/website/symbol-white.svg" alt="" aria-hidden="true" style={{ position: 'absolute', right: 60, top: -40, width: 280, height: 280, opacity: 0.08, animation: 'profSpin 80s linear infinite', pointerEvents: 'none' }} />

            <div style={{ position: 'absolute', top: 22, right: 24, display: 'flex', gap: 10, zIndex: 3, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setIsEditMode(true)}
                className="cd-btn"
                style={{ padding: '11px 18px', fontSize: 13, background: 'rgba(12,17,32,.5)' }}
              >
                <Edit size={16} />
                Edit profile
              </button>
            </div>
          </div>
          ); })()}

          {/* identity row — aligned to the 1180 content column, overlapping the cover */}
          <div style={{ maxWidth: 1180, margin: '0 auto', padding: '0 16px' }}>
          <div className="cd-col-mobile" style={{ display: 'flex', alignItems: 'flex-end', gap: 20, marginTop: -46, position: 'relative', zIndex: 2, flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', flex: '0 0 auto' }}>
              <div style={{ width: 108, height: 108, borderRadius: 26, overflow: 'hidden', border: '4px solid #0C1120', background: 'linear-gradient(135deg,#572B7C,#28C0D0)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {profilePhotoUrl ? (
                  <img src={profilePhotoUrl} alt={formData.full_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <span className="cd-display" style={{ fontWeight: 800, fontSize: 38, color: '#fff' }}>{initials}</span>
                )}
              </div>
              {uploadingPhoto && (
                <div style={{ position: 'absolute', inset: 0, borderRadius: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,.6)', color: '#fff', fontSize: 12 }}>Uploading…</div>
              )}
            </div>

            <div className="prof-id-text" style={{ flex: '1 1 340px', minWidth: 0, paddingBottom: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 11, flexWrap: 'wrap' }}>
                <span className="cd-display" style={{ fontWeight: 800, fontSize: 28, color: '#fff', letterSpacing: '-.6px' }}>
                  {formData.first_name && formData.last_name ? `${formData.first_name} ${formData.last_name}` : (formData.full_name || 'Your Name')}
                </span>
                {formData.wwcc && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700, color: '#67C6B4', background: 'rgba(103,198,180,.16)', padding: '4px 10px', borderRadius: 999 }}>
                    <Shield size={12} /> WWCC verified
                  </span>
                )}
              </div>
              <div style={{ fontSize: 14, color: 'rgba(255,255,255,.6)', marginTop: 4 }}>
                {formData.business_name || 'Business Name'}{formData.address ? ` · ${getCityFromAddress(formData.address)}` : ''}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 10, flexWrap: 'wrap' }}>
                {reviewStats.total_reviews > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ color: '#FCD34D', fontSize: 15, letterSpacing: 1 }}>
                      {[1, 2, 3, 4, 5].map((s) => (s <= Math.round(reviewStats.average_rating) ? '★' : '☆')).join('')}
                    </span>
                    <span className="cd-display" style={{ fontWeight: 700, fontSize: 14, color: '#fff' }}>{reviewStats.average_rating}</span>
                    <button type="button" onClick={scrollToReviews} style={{ background: 'none', border: 'none', padding: 0, fontSize: 12.5, color: '#28C0D0', cursor: 'pointer' }}>
                      Read {reviewStats.total_reviews} reviews →
                    </button>
                  </div>
                )}
                {user.user_number && (
                  <span className="cd-mono" style={{ fontSize: 11.5, color: 'rgba(255,255,255,.4)' }}>User ID #{user.user_number}</span>
                )}
              </div>
            </div>

            <div className="prof-id-roles" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end', paddingBottom: 6, flex: '0 1 auto', marginLeft: 'auto' }}>
              {formData.skills?.map((skill, i) => {
                const roleOption = roleOptions.find((r) => r.value === skill);
                const aqua = i % 2 === 0;
                return (
                  <span key={skill} style={{ fontSize: 11.5, fontWeight: 600, color: '#fff', background: aqua ? 'rgba(40,192,208,.16)' : 'rgba(120,40,124,.22)', border: aqua ? '1px solid rgba(40,192,208,.3)' : '1px solid rgba(120,40,124,.4)', padding: '6px 12px', borderRadius: 999 }}>
                    {roleOption?.label || skill}
                  </span>
                );
              })}
            </div>
          </div>
        </div>

        {/* ===== CONTENT ===== */}
        <div className="cd-main" style={{ paddingTop: 28 }}>
          {/* ===== VIEW MODE (always rendered; editing is a portaled modal) ===== */}
          <div className="prof-grid">
              {/* LEFT */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 18, minWidth: 0 }}>
                {formData.bio && (
                  <div style={{ background: 'rgba(255,255,255,.045)', border: '1px solid rgba(255,255,255,.09)', borderRadius: 18, padding: '22px 24px' }}>
                    <div className="cd-display" style={{ fontWeight: 700, fontSize: 15, color: '#fff', marginBottom: 10 }}>About me</div>
                    <div style={{ fontSize: 14, color: 'rgba(255,255,255,.7)', lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>{formData.bio}</div>
                  </div>
                )}

                <PortfolioGallery
                  userId={user.id}
                  portfolioImages={portfolioImages}
                  portfolioImageKeys={portfolioImageKeys}
                  isEditMode={false}
                  isDark={true}
                  onImagesUpdate={fetchPortfolioImages}
                />

                {isPhotographer && formData.portfolio_links?.length > 0 && (() => {
                  const links = formData.portfolio_links.filter((link) =>
                    link &&
                    !link.includes('facebook.com') &&
                    !link.includes('instagram.com') &&
                    !link.includes('twitter.com') &&
                    !link.includes('linkedin.com') &&
                    !link.match(/^https?:\/\/(www\.)?[^/]+\/?$/)
                  );
                  if (links.length === 0) return null;
                  return (
                    <div style={{ background: 'rgba(255,255,255,.045)', border: '1px solid rgba(255,255,255,.09)', borderRadius: 18, padding: '22px 24px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                        <div className="cd-display" style={{ fontWeight: 700, fontSize: 15, color: '#fff' }}>Photography portfolio</div>
                        <span className="cd-pill" style={{ color: '#28C0D0', background: 'rgba(40,192,208,.16)', fontWeight: 700 }}>{links.length} {links.length === 1 ? 'link' : 'links'}</span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                        {links.map((link, index) => (
                          <a key={index} href={link} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: 'rgba(255,255,255,.78)', textDecoration: 'none', background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.08)', borderRadius: 11, padding: '11px 13px' }}>
                            <ImageIcon size={16} color="#28C0D0" />
                            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{link}</span>
                            <span style={{ color: '#28C0D0' }}>→</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {isVideographer && formData.video_links?.length > 0 && (() => {
                  const links = formData.video_links.filter((link) =>
                    link &&
                    !link.includes('facebook.com') &&
                    !link.includes('instagram.com') &&
                    !link.includes('twitter.com') &&
                    !link.includes('linkedin.com') &&
                    !link.match(/^https?:\/\/(www\.)?[^/]+\/?$/)
                  );
                  if (links.length === 0) return null;
                  return (
                    <div style={{ background: 'rgba(255,255,255,.045)', border: '1px solid rgba(255,255,255,.09)', borderRadius: 18, padding: '22px 24px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                        <div className="cd-display" style={{ fontWeight: 700, fontSize: 15, color: '#fff' }}>Video portfolio</div>
                        <span className="cd-pill" style={{ color: '#C98AD4', background: 'rgba(120,40,124,.22)', fontWeight: 700 }}>{links.length} {links.length === 1 ? 'link' : 'links'}</span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                        {links.map((link, index) => (
                          <a key={index} href={link} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: 'rgba(255,255,255,.78)', textDecoration: 'none', background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.08)', borderRadius: 11, padding: '11px 13px' }}>
                            <Video size={16} color="#C98AD4" />
                            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{link}</span>
                            <span style={{ color: '#C98AD4' }}>→</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {isEditor && formData.editing_links?.length > 0 && (() => {
                  const links = formData.editing_links.filter((link) =>
                    link &&
                    !link.includes('facebook.com') &&
                    !link.includes('instagram.com') &&
                    !link.includes('twitter.com') &&
                    !link.includes('linkedin.com') &&
                    !link.match(/^https?:\/\/(www\.)?[^/]+\/?$/)
                  );
                  if (links.length === 0) return null;
                  return (
                    <div style={{ background: 'rgba(255,255,255,.045)', border: '1px solid rgba(255,255,255,.09)', borderRadius: 18, padding: '22px 24px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                        <div className="cd-display" style={{ fontWeight: 700, fontSize: 15, color: '#fff' }}>Editing portfolio</div>
                        <span className="cd-pill" style={{ color: '#67C6B4', background: 'rgba(103,198,180,.16)', fontWeight: 700 }}>{links.length} {links.length === 1 ? 'link' : 'links'}</span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                        {links.map((link, index) => (
                          <a key={index} href={link} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: 'rgba(255,255,255,.78)', textDecoration: 'none', background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.08)', borderRadius: 11, padding: '11px 13px' }}>
                            <ImageIcon size={16} color="#67C6B4" />
                            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{link}</span>
                            <span style={{ color: '#67C6B4' }}>→</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {formData.equipment_list?.filter(Boolean).length > 0 && (
                  <div style={{ background: 'rgba(255,255,255,.045)', border: '1px solid rgba(255,255,255,.09)', borderRadius: 18, padding: '22px 24px' }}>
                    <div className="cd-display" style={{ fontWeight: 700, fontSize: 15, color: '#fff', marginBottom: 13 }}>Equipment</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                      {formData.equipment_list.filter(Boolean).map((equipment, index) => (
                        <span key={index} style={{ fontSize: 11.5, fontWeight: 500, color: 'rgba(255,255,255,.8)', background: 'rgba(255,255,255,.06)', padding: '6px 12px', borderRadius: 999 }}>{equipment}</span>
                      ))}
                    </div>
                  </div>
                )}

                <div id="reviews-section">
                  <ReviewsList
                    userId={user.id}
                    reviews={reviews}
                    stats={reviewStats}
                    isDark={true}
                  />
                </div>
              </div>

              {/* RIGHT */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 18, minWidth: 0 }}>
                <div style={{ background: 'rgba(255,255,255,.045)', border: '1px solid rgba(255,255,255,.09)', borderRadius: 18, padding: '20px 22px' }}>
                  <div className="cd-display" style={{ fontWeight: 700, fontSize: 14, color: '#fff', marginBottom: 14 }}>Contact</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {formData.email && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                        <span style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(40,192,208,.14)', color: '#28C0D0', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}><Mail size={16} /></span>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 10.5, color: 'rgba(255,255,255,.45)' }}>Email</div>
                          <div style={{ fontSize: 13, color: '#fff', wordBreak: 'break-all' }}>{formData.email}</div>
                        </div>
                      </div>
                    )}
                    {formData.phone && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                        <span style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(103,198,180,.14)', color: '#67C6B4', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}><Phone size={16} /></span>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 10.5, color: 'rgba(255,255,255,.45)' }}>Phone</div>
                          <div className="cd-mono" style={{ fontSize: 13, color: '#fff' }}>{formData.phone}</div>
                        </div>
                      </div>
                    )}
                    {formData.address && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                        <span style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(120,40,124,.18)', color: '#C98AD4', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}><MapPin size={16} /></span>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 10.5, color: 'rgba(255,255,255,.45)' }}>Based in</div>
                          <div style={{ fontSize: 13, color: '#fff' }}>{getCityFromAddress(formData.address)}</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ background: 'rgba(255,255,255,.045)', border: '1px solid rgba(255,255,255,.09)', borderRadius: 18, padding: '20px 22px' }}>
                  <div className="cd-display" style={{ fontWeight: 700, fontSize: 14, color: '#fff', marginBottom: 14 }}>Business details</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
                    {formData.business_name && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                        <span style={{ fontSize: 12.5, color: 'rgba(255,255,255,.5)' }}>Business name</span>
                        <span style={{ fontSize: 12.5, color: '#fff', fontWeight: 600, textAlign: 'right' }}>{formData.business_name}</span>
                      </div>
                    )}
                    {formData.abn && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                        <span style={{ fontSize: 12.5, color: 'rgba(255,255,255,.5)' }}>ABN</span>
                        <span className="cd-mono" style={{ fontSize: 12.5, color: '#fff' }}>{formData.abn}</span>
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 12.5, color: 'rgba(255,255,255,.5)' }}>GST</span>
                      {formData.gst_registered ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700, color: '#67C6B4', background: 'rgba(103,198,180,.16)', padding: '2px 9px', borderRadius: 999 }}>Registered</span>
                      ) : (
                        <span style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,.55)', background: 'rgba(255,255,255,.06)', padding: '2px 9px', borderRadius: 999 }}>Not registered</span>
                      )}
                    </div>
                  </div>
                </div>

                {(formData.website_url || formData.facebook_url || formData.instagram_url) && (
                  <div style={{ background: 'rgba(255,255,255,.045)', border: '1px solid rgba(255,255,255,.09)', borderRadius: 18, padding: '20px 22px' }}>
                    <div className="cd-display" style={{ fontWeight: 700, fontSize: 14, color: '#fff', marginBottom: 13 }}>Links</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                      {formData.website_url && (
                        <a href={formData.website_url} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: 'rgba(255,255,255,.78)', textDecoration: 'none' }}>
                          <Globe size={15} />
                          <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{formData.website_url.replace(/^https?:\/\//, '')}</span>
                        </a>
                      )}
                      {formData.facebook_url && (
                        <a href={formData.facebook_url} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: 'rgba(255,255,255,.78)', textDecoration: 'none' }}>
                          <Globe size={15} />
                          <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{formData.facebook_url.replace(/^https?:\/\/(www\.)?facebook\.com\//, '@')}</span>
                        </a>
                      )}
                      {formData.instagram_url && (
                        <a href={formData.instagram_url} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: 'rgba(255,255,255,.78)', textDecoration: 'none' }}>
                          <Globe size={15} />
                          <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{formData.instagram_url.replace(/^https?:\/\/(www\.)?instagram\.com\//, '@')}</span>
                        </a>
                      )}
                    </div>
                  </div>
                )}

                {isDroneOperator && (formData.casa_repl || formData.casa_reoc) && (
                  <div style={{ background: 'rgba(255,255,255,.045)', border: '1px solid rgba(255,255,255,.09)', borderRadius: 18, padding: '20px 22px' }}>
                    <div className="cd-display" style={{ fontWeight: 700, fontSize: 14, color: '#fff', marginBottom: 13 }}>CASA certifications</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {formData.casa_repl && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <CheckCircle2 size={18} color="#67C6B4" />
                          <span style={{ fontSize: 13, color: 'rgba(255,255,255,.85)' }}>RePL certified</span>
                        </div>
                      )}
                      {formData.casa_reoc && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <CheckCircle2 size={18} color="#67C6B4" />
                          <span style={{ fontSize: 13, color: 'rgba(255,255,255,.85)' }}>ReOC certified</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          {/* ===== EDIT PROFILE MODAL (portaled to document.body) ===== */}
          {isEditMode && createPortal(
            <div
              onMouseDown={(e) => { if (e.target === e.currentTarget) setIsEditMode(false); }}
              style={{ position: 'fixed', inset: 0, zIndex: 4000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 20, background: 'rgba(8,11,20,.66)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', overflowY: 'auto' }}
            >
              <form
                id="profile-form"
                className="prof-modal-card"
                onSubmit={handleSubmit}
                onMouseDown={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA' && !e.target.hasAttribute('data-google-autocomplete')) {
                    e.preventDefault();
                  }
                }}
                style={{ width: '100%', maxWidth: 560, margin: 'auto', display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 40px)', background: '#12162A', border: '1px solid rgba(255,255,255,.12)', borderRadius: 22, boxShadow: '0 40px 90px -30px rgba(0,0,0,.85)', overflow: 'hidden', animation: 'profModalIn .24s cubic-bezier(.2,.85,.25,1)' }}
              >
                {/* Sticky header */}
                <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '18px 24px', borderBottom: '1px solid rgba(255,255,255,.08)', background: '#12162A' }}>
                  <div>
                    <div className="cd-display" style={{ fontWeight: 800, fontSize: 18, color: '#fff', letterSpacing: '-.4px' }}>Edit profile</div>
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,.45)', marginTop: 2 }}>{userStatus === 'NEW' ? 'Complete your profile to submit for approval' : 'Update your details, portfolio and certifications'}</div>
                  </div>
                  <button type="button" onClick={() => setIsEditMode(false)} aria-label="Close" style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,255,255,.16)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flex: '0 0 auto' }}>
                    <X size={17} />
                  </button>
                </div>

                {/* Scrollable body */}
                <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '20px 24px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

                    {/* Photo + identity */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                      <span style={{ width: 60, height: 60, borderRadius: 16, overflow: 'hidden', background: 'linear-gradient(135deg,#572B7C,#28C0D0)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', flex: '0 0 auto' }}>
                        {profilePhotoUrl ? (
                          <img src={profilePhotoUrl} alt={formData.full_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <span className="cd-display" style={{ fontWeight: 800, fontSize: 20 }}>{initials}</span>
                        )}
                      </span>
                      <input type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={handleFileSelect} style={{ display: 'none' }} id="photo-upload" />
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <label htmlFor="photo-upload" className="cd-btn" style={{ padding: '10px 16px', fontSize: 12.5, cursor: 'pointer' }}>
                          {uploadingPhoto ? <><Loader2 size={15} className="animate-spin" /> Uploading…</> : <><Upload size={15} /> Change photo</>}
                        </label>
                        {profilePhotoUrl && (
                          <button type="button" onClick={handleDeletePhoto} className="cd-btn" style={{ padding: '10px 14px', fontSize: 12.5, color: 'rgba(255,255,255,.6)' }}>Remove</button>
                        )}
                      </div>
                    </div>
                    <p style={{ fontSize: 11.5, color: 'rgba(255,255,255,.4)', margin: 0 }}>Max 10MB. JPG, PNG, GIF or WebP.</p>

                    <div style={dividerStyle} />

                    {/* BASIC INFO */}
                    <div style={sectionLabelStyle}>BASIC INFO</div>
                    <div className="prof-2col">
                      <div><label style={labelStyle}>First name *</label><input className="cd-input" style={{ padding: '11px 13px' }} value={formData.first_name} onChange={(e) => handleChange('first_name', e.target.value)} required /></div>
                      <div><label style={labelStyle}>Last name *</label><input className="cd-input" style={{ padding: '11px 13px' }} value={formData.last_name} onChange={(e) => handleChange('last_name', e.target.value)} required /></div>
                    </div>
                    <div><label style={labelStyle}>Business name</label><input className="cd-input" style={{ padding: '11px 13px' }} value={formData.business_name} onChange={(e) => handleChange('business_name', e.target.value)} /></div>
                    <div className="prof-2col">
                      <div><label style={labelStyle}>Email *</label><input className="cd-input" style={{ padding: '11px 13px', background: 'rgba(255,255,255,.02)', color: 'rgba(255,255,255,.6)' }} type="email" value={formData.email} onChange={(e) => handleChange('email', e.target.value)} required disabled /></div>
                      <div><label style={labelStyle}>Phone *</label><input className="cd-input cd-mono" style={{ padding: '11px 13px' }} value={formData.phone} onChange={(e) => handleChange('phone', e.target.value)} required /></div>
                    </div>
                    <div>
                      <label style={labelStyle}>Base location *</label>
                      <div id="profile-address-autocomplete" style={{ marginBottom: 10 }}></div>
                      {formData.address && formData.location_lat && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 11, borderRadius: 11, background: 'rgba(103,198,180,.12)', border: '1px solid rgba(103,198,180,.3)', marginBottom: 10 }}>
                          <MapPin size={15} color="#67C6B4" />
                          <p style={{ fontSize: 12.5, color: '#fff', fontWeight: 500, margin: 0 }}>✓ {formData.address}</p>
                        </div>
                      )}
                      <p style={{ fontSize: 11, color: 'rgba(255,255,255,.4)', margin: 0 }}>Your full address is not disclosed to users — it's only for invoicing and accounting.</p>
                    </div>
                    <div><label style={labelStyle}>About</label><textarea className="cd-input" style={{ padding: '11px 13px', resize: 'none', lineHeight: 1.5 }} value={formData.bio} onChange={(e) => handleChange('bio', e.target.value)} placeholder="Tell us about yourself, your experience, and what makes you unique…" rows={3} /></div>
                    <div>
                      <label style={{ ...labelStyle, marginBottom: 8 }}>Roles *</label>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {roleOptions.map((role) => {
                          const isSelected = formData.skills?.includes(role.value);
                          return (
                            <button type="button" key={role.value} onClick={() => handleRoleToggle(role.value)}
                              style={isSelected
                                ? { fontSize: 12, fontWeight: 600, color: '#0C1120', background: '#28C0D0', border: '1px solid #28C0D0', padding: '7px 13px', borderRadius: 999, cursor: 'pointer' }
                                : { fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,.7)', background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.1)', padding: '7px 13px', borderRadius: 999, cursor: 'pointer' }}>
                              {role.label}{isSelected ? ' ✕' : ''}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div style={dividerStyle} />

                    {/* PORTFOLIO · DRAG TO REORDER (component renders its own section label) */}
                    <PortfolioGallery
                      userId={user.id}
                      portfolioImages={portfolioImages}
                      portfolioImageKeys={portfolioImageKeys}
                      isEditMode={true}
                      isDark={true}
                      onImagesUpdate={fetchPortfolioImages}
                    />

                    {isPhotographer && (
                      <div>
                        <label style={{ ...labelStyle, marginBottom: 8 }}>Photo portfolio links</label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {formData.portfolio_links.map((link, idx) => (
                            <div key={idx} style={{ display: 'flex', gap: 8 }}>
                              <input className="cd-input" style={{ padding: '11px 13px' }} value={link} onChange={(e) => handleLinkChange('portfolio_links', idx, e.target.value)} placeholder="https://example.com/portfolio" />
                              <button type="button" onClick={() => handleRemoveLink('portfolio_links', idx)} className="cd-btn" style={{ padding: '0 14px', color: '#E5848A' }}><X size={15} /></button>
                            </div>
                          ))}
                          <button type="button" onClick={() => handleAddLink('portfolio_links')} className="cd-btn" style={{ width: '100%', padding: '11px' }}><Plus size={15} /> Add portfolio link</button>
                        </div>
                      </div>
                    )}

                    {isVideographer && (
                      <div>
                        <label style={{ ...labelStyle, marginBottom: 8 }}>Showcase video links</label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {formData.video_links.map((link, idx) => (
                            <div key={idx} style={{ display: 'flex', gap: 8 }}>
                              <input className="cd-input" style={{ padding: '11px 13px' }} value={link} onChange={(e) => handleLinkChange('video_links', idx, e.target.value)} placeholder="https://youtube.com/watch?v=..." />
                              <button type="button" onClick={() => handleRemoveLink('video_links', idx)} className="cd-btn" style={{ padding: '0 14px', color: '#E5848A' }}><X size={15} /></button>
                            </div>
                          ))}
                          <button type="button" onClick={() => handleAddLink('video_links')} className="cd-btn" style={{ width: '100%', padding: '11px' }}><Plus size={15} /> Add video link</button>
                        </div>
                      </div>
                    )}

                    {isEditor && (
                      <div>
                        <label style={{ ...labelStyle, marginBottom: 8 }}>Editing showcase links</label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {formData.editing_links.map((link, idx) => (
                            <div key={idx} style={{ display: 'flex', gap: 8 }}>
                              <input className="cd-input" style={{ padding: '11px 13px' }} value={link} onChange={(e) => handleLinkChange('editing_links', idx, e.target.value)} placeholder="https://example.com/editing-work" />
                              <button type="button" onClick={() => handleRemoveLink('editing_links', idx)} className="cd-btn" style={{ padding: '0 14px', color: '#E5848A' }}><X size={15} /></button>
                            </div>
                          ))}
                          <button type="button" onClick={() => handleAddLink('editing_links')} className="cd-btn" style={{ width: '100%', padding: '11px' }}><Plus size={15} /> Add editing link</button>
                        </div>
                      </div>
                    )}

                    <div style={dividerStyle} />

                    {/* BUSINESS & COMPLIANCE */}
                    <div style={sectionLabelStyle}>BUSINESS &amp; COMPLIANCE</div>

                    {/* ABN validation — wired to the REAL POST /api/abn/validate flow */}
                    <div>
                      <label style={labelStyle}>ABN</label>
                      <div style={{ display: 'flex', gap: 9 }}>
                        <input
                          className="cd-input cd-mono"
                          style={{ flex: 1, minWidth: 0, padding: '11px 13px' }}
                          inputMode="numeric"
                          value={formData.abn}
                          onChange={(e) => { handleChange('abn', formatABN(e.target.value)); if (abnError) setAbnError(''); }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const d = (formData.abn || '').replace(/\D/g, '');
                              if (d.length !== 11) { setAbnError('Enter a valid 11-digit ABN.'); return; }
                              setAbnError('');
                              handleValidateABN(formData.abn);
                            }
                          }}
                          placeholder="12 345 678 901"
                          maxLength={14}
                          disabled={abnResult && abnResult.is_valid}
                        />
                        {abnResult && abnResult.is_valid ? (
                          <button
                            type="button"
                            onClick={() => { setAbnResult(null); setBusinessNames([]); setAbnError(''); handleChange('abn', ''); handleChange('business_name', ''); }}
                            className="cd-btn"
                            style={{ flex: '0 0 auto', padding: '0 16px', fontSize: 12.5, color: '#E5848A' }}
                          >
                            <X size={15} /> Clear
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => {
                              const d = (formData.abn || '').replace(/\D/g, '');
                              if (d.length !== 11) { setAbnError('Enter a valid 11-digit ABN.'); return; }
                              setAbnError('');
                              handleValidateABN(formData.abn);
                            }}
                            disabled={abnValidating}
                            style={{ flex: '0 0 auto', padding: '0 18px', borderRadius: 11, background: '#28C0D0', border: 'none', color: '#0C1120', fontFamily: "'Work Sans', sans-serif", fontWeight: 700, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, opacity: abnValidating ? 0.7 : 1 }}
                          >
                            {abnValidating ? (<><Loader2 size={14} className="animate-spin" /> Checking…</>) : 'Validate'}
                          </button>
                        )}
                      </div>
                      {abnError && <div style={{ fontSize: 11.5, color: '#F87171', marginTop: 7 }}>{abnError}</div>}

                      {/* Result card — populated from the real ABR response (status, GST, business names) */}
                      {abnResult && abnResult.is_valid && (
                        <div style={{ marginTop: 11, border: '1px solid rgba(103,198,180,.22)', background: 'rgba(103,198,180,.07)', borderRadius: 12, padding: '13px 15px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 11 }}>
                            <CheckCircle2 size={15} color="#67C6B4" />
                            <span style={{ fontSize: 12.5, fontWeight: 700, color: '#67C6B4' }}>
                              {abnResult.abn_status || 'Active'}{abnResult.gst_from_date ? ` · since ${new Date(abnResult.gst_from_date).getFullYear()}` : ''}
                            </span>
                            <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 600, color: '#67C6B4', background: 'rgba(103,198,180,.16)', padding: '3px 9px', borderRadius: 999 }}>
                              {abnResult.gst_registered ? 'Registered for GST' : 'Not registered for GST'}
                            </span>
                          </div>
                          <div style={{ fontSize: 11, color: 'rgba(255,255,255,.5)', marginBottom: 5 }}>Registered business name</div>
                          <div style={{ position: 'relative' }}>
                            <select className="prof-abn-select" value={formData.business_name} onChange={(e) => handleChange('business_name', e.target.value)} required>
                              {!formData.business_name && <option value="" style={{ color: '#0C1120' }}>Select business name…</option>}
                              {businessNames.map((name, idx) => (
                                <option key={idx} value={name} style={{ color: '#0C1120' }}>{name}</option>
                              ))}
                            </select>
                            <ChevronDown size={16} color="rgba(255,255,255,.5)" style={{ position: 'absolute', right: 11, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                          </div>
                          <p style={{ fontSize: 11, color: 'rgba(255,255,255,.4)', margin: '8px 0 0' }}>This name appears on invoices and contracts.</p>
                        </div>
                      )}
                    </div>

                    {/* Equipment (free-text items — kept editable to preserve the real add/remove wiring) */}
                    <div>
                      <label style={{ ...labelStyle, marginBottom: 8 }}>Equipment</label>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {formData.equipment_list.map((item, idx) => (
                          <div key={idx} style={{ display: 'flex', gap: 8 }}>
                            <input className="cd-input" style={{ padding: '11px 13px' }} value={item} onChange={(e) => handleLinkChange('equipment_list', idx, e.target.value)} placeholder="e.g., Panasonic S5iix, 70-200mm lens, DJI gimbal" />
                            <button type="button" onClick={() => handleRemoveLink('equipment_list', idx)} className="cd-btn" style={{ padding: '0 14px', color: '#E5848A' }}><X size={15} /></button>
                          </div>
                        ))}
                        <button type="button" onClick={() => handleAddLink('equipment_list')} className="cd-btn" style={{ width: '100%', padding: '11px' }}><Plus size={15} /> Add equipment</button>
                      </div>
                    </div>

                    {/* Certifications */}
                    <div>
                      <label style={{ ...labelStyle, marginBottom: 8 }}>Certifications</label>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: formData.wwcc ? '#67C6B4' : 'rgba(255,255,255,.7)', background: formData.wwcc ? 'rgba(103,198,180,.12)' : 'rgba(255,255,255,.06)', border: formData.wwcc ? '1px solid rgba(103,198,180,.25)' : '1px solid rgba(255,255,255,.1)', padding: '7px 13px', borderRadius: 999 }}>
                        <input type="checkbox" checked={formData.wwcc === true} onChange={(e) => handleChange('wwcc', e.target.checked === true)} style={{ width: 14, height: 14, accentColor: '#67C6B4' }} />
                        <Shield size={13} /> WWCC verified
                      </label>
                      <p style={{ fontSize: 11, color: 'rgba(255,255,255,.4)', margin: '8px 0 0' }}>Required for jobs involving children or minors.</p>
                      {isDroneOperator && (
                        <div style={{ marginTop: 12 }}>
                          <label style={{ ...labelStyle, marginBottom: 8 }}>CASA certifications (select at least one)</label>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', color: 'rgba(255,255,255,.85)', fontSize: 13 }}>
                              <input type="checkbox" checked={formData.casa_repl === true} onChange={(e) => handleChange('casa_repl', e.target.checked === true)} style={{ width: 16, height: 16, accentColor: '#28C0D0' }} />
                              CASA RePL (Remote Pilot Licence)
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', color: 'rgba(255,255,255,.85)', fontSize: 13 }}>
                              <input type="checkbox" checked={formData.casa_reoc === true} onChange={(e) => handleChange('casa_reoc', e.target.checked === true)} style={{ width: 16, height: 16, accentColor: '#28C0D0' }} />
                              CASA ReOC (Remote Operator Certificate)
                            </label>
                          </div>
                        </div>
                      )}
                    </div>

                    <div style={dividerStyle} />

                    {/* LINKS */}
                    <div style={sectionLabelStyle}>LINKS</div>
                    <div><label style={labelStyle}>Website</label><input className="cd-input" style={{ padding: '11px 13px' }} value={formData.website_url} onChange={(e) => handleChange('website_url', e.target.value)} placeholder="https://yourwebsite.com" /></div>
                    <div className="prof-2col">
                      <div><label style={labelStyle}>Facebook</label><input className="cd-input" style={{ padding: '11px 13px' }} value={formData.facebook_url} onChange={(e) => handleChange('facebook_url', e.target.value)} placeholder="https://facebook.com/yourprofile" /></div>
                      <div><label style={labelStyle}>Instagram</label><input className="cd-input" style={{ padding: '11px 13px' }} value={formData.instagram_url} onChange={(e) => handleChange('instagram_url', e.target.value)} placeholder="@yourhandle" /></div>
                    </div>

                  </div>
                </div>

                {/* Sticky footer */}
                <div style={{ flexShrink: 0, display: 'flex', gap: 12, padding: '15px 22px calc(15px + env(safe-area-inset-bottom))', borderTop: '1px solid rgba(255,255,255,.08)', background: '#12162A' }}>
                  <button type="button" onClick={() => setIsEditMode(false)} className="cd-btn" style={{ flex: 1, padding: '13px', fontSize: 14 }}>Cancel</button>
                  <button
                    type="submit"
                    onClick={() => {
                      console.log('💾 Bottom Save Changes button clicked');
                      setSaveButtonClicked(true);
                    }}
                    className="cd-btn cd-btn-primary"
                    style={{ flex: 1.6, padding: '13px', fontSize: 14 }}
                  >
                    {userStatus === 'NEW' ? 'Submit profile for approval' : 'Save changes'}
                  </button>
                </div>
              </form>
            </div>,
            document.body
          )}
        </div>
      </div>

      {/* Pending Approval Modal */}
      <PendingApprovalModal
        show={showPendingModal}
        userEmail={formData.email}
        onClose={() => setShowPendingModal(false)}
      />

      <ProfileSubmittedModal
        isOpen={showSubmittedModal}
        onClose={() => {
          setShowSubmittedModal(false);
          // Optionally navigate to dashboard
          window.location.href = '/dashboard';
        }}
      />

      {/* Welcome Modal for NEW users */}
      <WelcomeModal
        show={showWelcomeModal}
        userName={formData.first_name || user.first_name || user.full_name}
        onClose={() => setShowWelcomeModal(false)}
        destination="/onboarding?step=2"
        userId={user.id}
      />
    </div>
  );
};

export default Profile;
